import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "How much does a custom SaaS application cost?",
      answer: "At SVNATE, the cost of custom SaaS applications varies depending on complexity, features, and scale. We provide tailored estimates after our initial discovery call to ensure you get exactly what you need within your budget."
    },
    {
      question: "How long does it take to build a website or app?",
      answer: "A standard corporate website takes 2-4 weeks. Complex mobile applications and SaaS platforms typically take 2-4 months from concept to launch."
    },
    {
      question: "Do you provide ongoing maintenance and support?",
      answer: "Yes, we offer comprehensive post-launch support, maintenance, and hosting packages to ensure your digital product remains fast, secure, and up-to-date."
    },
    {
      question: "What technologies do you use for web and app development?",
      answer: "We specialize in modern, high-performance stacks. For web development, we use React, Next.js, Node.js, and TypeScript. For robust cross-platform mobile app development, we build with Flutter and React Native."
    }
  ];

  return (
    <section className="bg-svnate-darkNavy py-24 px-6 md:px-12 relative" id="faq">
      <div className="container mx-auto max-w-4xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-white/10 bg-[#0f1419] ">
            <span className="text-svnate-lightTeal font-semibold text-sm">FAQ</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
            Common Questions
          </h2>
          <p className="text-lg md:text-xl text-foreground/70">
            Everything you need to know about working with SVNATE.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="bg-[#0f1419] border border-white/10 rounded-2xl overflow-hidden "
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                className="w-full px-6 py-6 md:px-8 flex items-center justify-between text-left focus:outline-none hover:bg-[#0f1419] transition-colors"
              >
                <h3 className={`text-lg md:text-xl font-semibold pr-8 transition-colors duration-300 ${openIndex === index ? 'text-white' : 'text-foreground/80'}`}>
                  {faq.question}
                </h3>
                <div className={`p-2 rounded-full border transition-all duration-300 ${openIndex === index ? 'bg-svnate-teal border-svnate-teal text-black rotate-180' : 'border-white/20 text-white/50'}`}>
                  {openIndex === index ? <Minus size={20} /> : <Plus size={20} />}
                </div>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 md:px-8 md:pb-8 pt-0">
                      <p className="text-foreground/70 leading-relaxed pl-4 border-l-2 border-svnate-teal/50">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
